import { useLazyQuery } from 'apollo-client-ppc';
import { format, fromUnixTime } from 'date-fns';
import capitalize from 'lodash/capitalize';
import defaultTo from 'lodash/defaultTo';
import { token } from '@atlaskit/tokens';
import EmptyState from '@atlaskit/empty-state';
import get from 'lodash/get';
import keys from 'lodash/keys';
import pickBy from 'lodash/pickBy';
import startCase from 'lodash/startCase';
import toUpper from 'lodash/toUpper';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import Button from '@atlaskit/button';
import Popup from '@atlaskit/popup';
import { FormFooter } from '@atlaskit/form';
import ArrowLeft from '@atlaskit/icon/glyph/arrow-left';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import {
  CustomPanel,
  ColGroup,
  EntitlementIcon,
  FormattedDate,
  SearchResultsTable,
  TableBody,
  TableHead,
  useSidebar,
  useFeatureToggle,
  OpenSidebarButton,
} from '@css-xp/base';
import styled from '@emotion/styled';
import Select, { ValueType } from '@atlaskit/select';
//@ts-ignore
import PartnerDiscountIndicationIcon from '@css-xp/base/assets/img/partner-discounts-indication.svg';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { PricingPlan } from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { fetchAvailablePricingPlansQuery } from '@css-xp/products-v2';
import { ProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { NEW_DATE_FORMAT, PricingPlanStatus } from '@atlassian/cssxp-typescommon';
import { EntitlementEdition } from '@atlassian/cssxp-utils-entitlement';
import { fetchOfferingForProductForOrderReview } from '@css-xp/ppc-checkout';
import { BITBUCKET_PRODUCT_ID, CATALOGUE_CONSTANTS, ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { emptyStatePropForPricingPlanTable } from '@css-xp/ppc-common/src/state-props';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants/src/ppc-feature-gates';
import { isCollectionProductEntitlement, isOfferingIncluded } from '@css-xp/ppc-utils';
import { ProductOffering } from '@css-xp/graphtypes/src/generated-types-sxp';
import { CollapsibleSummaryTableLoader } from '@css-xp/ppc-page-loaders';
import { PricingTiersTable } from './pricing-tiers-table';
import { IncludedProductsSidebar } from './included-products-sidebar';
import './product-pricing-pane.scss';

interface ProductPricingPaneProps {
  txaId?: string;
  product: ProductCatalogueItem;
  hasOptedInForPartnerPromotion?: boolean;
  isEnterpriseOfferingEnabled?: boolean;
  isJSMRepackagingEnabled?: boolean;
  isCompassPremiumEnabled?: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
  version?: number | null;
  showDefaultAnnualPricing?: boolean;
  isTWCPhase2Enabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
  isMultiInstanceAppEnabled?: boolean;
}

const CustomPricingPlanPanel = styled.div`
  padding-top: ${token('space.300')};
  .custom-panel-wrapper-container {
    .custom-panel-wrapper-v2 > div > div:first-of-type {
      padding-left: ${token('space.500')};
      background-color: ${token('color.background.accent.gray.subtlest')};
    }
  }
`;

const StyledPricingHeader = styled.div`
  .pricing-header {
    margin-top: ${token('space.0')};
  }
`;

const EmptyPricingPlanTable = styled.div`
  padding-top: ${token('space.500')};
  color: ${token('color.text.disabled')};
`;

interface SelectItem {
  label: string;
  value: string;
}

interface PricingPlanTableProps {
  pricingPlans: PricingPlan[];
  error?: string | undefined;
  loading?: boolean;
  currentFilter: PricingFilter;
  setCurrentPricingPlan?: (pricing: PricingPlan) => void;
  product?: ProductCatalogueItem;
}

enum SyntheticPlanType {
  COMMERCIAL = 'COMMERCIAL',
  ACADEMIC = 'ACADEMIC',
  COMMUNITY = 'COMMUNITY',
  OPEN_SOURCE = 'OPEN_SOURCE',
  CLASSROOM = 'CLASSROOM',
}

const SANDBOX = 'SANDBOX';

enum Currency {
  USD = 'USD',
  JPY = 'JPY',
}

enum BillingCycle {
  MONTHLY = 'MONTHLY',
  ANNUAL = 'ANNUAL',
}

enum PricingPlanTypes {
  DEFAULT_PRICING = 'DEFAULT_PRICING',
}

enum PromotionTypes {
  PARTNER_DISCOUNT = 'PARTNER_DISCOUNT',
}

interface PricingFilter {
  [key: string]: string | null | BillingCycle | Currency | SyntheticPlanType;
}

export const PricingPlanTable = ({
  pricingPlans,
  error,
  loading,
  setCurrentPricingPlan,
  currentFilter,
}: PricingPlanTableProps) => {
  const updatedFilter = useMemo(() => {
    return { ...currentFilter, pricingPlanType: PricingPlanTypes.DEFAULT_PRICING, status: PricingPlanStatus.ACTIVE };
  }, [currentFilter]);

  const getHead = () => {
    const cells = [
      {
        name: 'Product',
        customClassNames: 'col-60',
      },
      {
        name: 'Pricing plan type',
        customClassNames: 'col-15',
      },
      {
        name: 'Last modified',
        customClassNames: 'col-15',
      },
    ];

    return cells;
  };

  const pricingPlansWithFilters = React.useMemo(() => {
    // checks if there are any truthy values in filter object
    const activeFilters = keys(pickBy(updatedFilter));
    let filteredResult = [...pricingPlans];
    activeFilters.forEach((filterName) => {
      filteredResult = defaultTo(filteredResult, []).filter(
        (plan) => get(plan, filterName) === get(updatedFilter, filterName)
      );
    });
    return filteredResult;
  }, [pricingPlans, updatedFilter]);

  const hasNoResults = pricingPlansWithFilters.length === 0;

  return (
    <SearchResultsTable
      id="pricing-plan-table"
      entityName="pricing plans"
      noResults={hasNoResults}
      isLoading={!!loading}
      error={error}
    >
      <ColGroup columns={getHead()} />
      <TableHead columns={getHead()} />
      <TableBody>
        {defaultTo(pricingPlansWithFilters, []).map((item: PricingPlan, index: any) => {
          const updatedAt = fromUnixTime(Number(get(item, 'updatedAt')) / 1000);
          const updateDate = `${format(updatedAt, 'yyyy')}-${format(updatedAt, 'MM')}-${format(updatedAt, 'dd')}`;
          return (
            <tr key={index}>
              <td>
                <Button
                  appearance="link"
                  onClick={() => {
                    if (setCurrentPricingPlan) {
                      setCurrentPricingPlan(item);
                    }
                  }}
                  testId={`pricing-plan-row-${index}`}
                >
                  {defaultTo(get(item, 'description'), 'Not available')}
                </Button>
              </td>
              <td data-testid={`pricing-plan-type-cell-${index}`}>
                <Lozenge isBold testId={`pricing-plan-type-${index}`}>
                  DEFAULT
                </Lozenge>
              </td>
              <td data-testid="item-amount-cell">
                <FormattedDate date={updateDate} format={NEW_DATE_FORMAT} />
              </td>
            </tr>
          );
        })}
      </TableBody>
    </SearchResultsTable>
  );
};

const getPricingPlanPanel = ({ pricingPlans, currentFilter, product }: PricingPlanTableProps) => {
  const updatedFilter = {
    ...currentFilter,
    pricingPlanType: PricingPlanTypes.DEFAULT_PRICING,
    status: PricingPlanStatus.ACTIVE,
  };

  const filterPricingPlans = (pricingPlans: PricingPlan[], updatedFilter: any) => {
    const activeFilters = keys(pickBy(updatedFilter));
    let filteredResult = [...pricingPlans];
    activeFilters.forEach((filterName) => {
      filteredResult = defaultTo(filteredResult, []).filter(
        (plan) => get(plan, filterName) === get(updatedFilter, filterName)
      );
    });
    return filteredResult;
  };

  const pricingPlansWithFilters = filterPricingPlans(pricingPlans, updatedFilter);

  const hasNoResults = pricingPlansWithFilters.length === 0;

  if (hasNoResults) {
    return (
      <div className="no-data-view">
        <EmptyState {...emptyStatePropForPricingPlanTable} />
      </div>
    );
  }

  const getHeader = (pricingPlan: PricingPlan) => {
    return (
      <div className="hello-test">
        <Flex alignItems="center" gap="space.150">
          <strong>{defaultTo(get(pricingPlan, 'description'), 'Not available')}</strong>
          <Lozenge isBold testId={`pricing-plan-type`}>
            DEFAULT
          </Lozenge>
        </Flex>
      </div>
    );
  };

  return (
    <CustomPricingPlanPanel>
      {defaultTo(pricingPlansWithFilters, []).map((pricingPlan, index) => (
        <CustomPanel
          key={index}
          expandedHeader={getHeader(pricingPlan)}
          collapsedHeader={getHeader(pricingPlan)}
          version={2}
          isExpanded
        >
          <Box padding="space.250">
            <PricingTiersTable pricingPlan={pricingPlan} product={product} version={2} />
          </Box>
        </CustomPanel>
      ))}
    </CustomPricingPlanPanel>
  );
};

export const ProductPricingPane = ({
  product,
  hasOptedInForPartnerPromotion,
  isEnterpriseOfferingEnabled = false,
  isJSMRepackagingEnabled = false,
  isCompassPremiumEnabled = false,
  isJPDAsJiraFamilyEnabled = false,
  version,
  showDefaultAnnualPricing = false,
  isTWCPhase2Enabled = false,
  isAppEditionSupportEnabled = false,
  isMultiInstanceAppEnabled = false,
}: ProductPricingPaneProps) => {
  const isVersion2 = version === 2;
  const [currentOffering, setCurrentOffering] = React.useState<string | undefined>();
  const [availableOfferingForProduct, setAvailableOfferingForProduct] = React.useState();
  const [availablePricingPlans, setAvailablePricingPlans] = React.useState();
  const [currentFrequency, setCurrentFrequency] = React.useState<string | BillingCycle | null>(
    showDefaultAnnualPricing ? BillingCycle.ANNUAL : BillingCycle.MONTHLY
  );
  const [currentPricingPlan, setCurrentPricingPlan] = React.useState<PricingPlan | null>(null);
  const [currentCurrency, setCurrentCurrency] = React.useState<string | undefined | null>(Currency.USD);
  const [currentFilter, setCurrentFilter] = React.useState<PricingFilter>({});
  const [isIncludedProductsPopupOpen, setIsIncludedProductsPopupOpen] = React.useState(false);

  const [isEnabled] = useFeatureToggle();
  const isJPDPremiumOfferingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_PREMIUM_OFFERING);
  const isAddOnsSearchEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2);
  const isStatuspageEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STATUSPAGE);
  const isTeamWorkCollectionEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isStrategyCollectionEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STRATEGY_COLLECTION);
  const isBitbucketProductEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_BITBUCKET);
  const sxpClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.SXP), []);

  const [fetchOfferingForProduct, { loading: loadingOfferingForProduct }] = useLazyQuery(
    fetchOfferingForProductForOrderReview,
    {
      client: sxpClient as any,
    }
  );
  const [fetchAvailablePricingPlans, { called, loading: pricingPlanLoading, error }] = useLazyQuery(
    fetchAvailablePricingPlansQuery,
    {
      client: sxpClient as any,
    }
  );

  const { closeSidebar } = useSidebar();
  React.useEffect(() => {
    (async () => {
      if (get(product, 'id')) {
        const { data } = await fetchOfferingForProduct({
          variables: {
            id: get(product, 'id'),
            isAddOnsSearchEnabled,
            isStatuspageEnabled,
            isTeamWorkCollectionEnabled,
            isStrategyCollectionEnabled,
            isBitbucketProductEnabled,
          },
        });
        setAvailableOfferingForProduct(data);
      }
    })();
  }, [product]);

  // filter sandbox offerings for partners
  const isValidOffering = (offeringName: string) => {
    return !offeringName.includes(capitalize(SANDBOX));
  };

  const pricingPlanOptions = React.useMemo(() => {
    const relatedOfferingsPricingPlans = defaultTo(get(availablePricingPlans, 'nativeCatalogItem.offering'), []).filter(
      (offering: any) => get(offering, 'id') === currentOffering
    );
    return defaultTo(get(relatedOfferingsPricingPlans, '0.availablePricingPlans'), []);
  }, [availablePricingPlans]);

  const syntheticPlanSelectOptions = React.useMemo(() => {
    const options: SelectItem[] = [];
    for (const [value, label] of Object.entries(SyntheticPlanType)) {
      options.push({ label: capitalize(startCase(label)), value });
    }

    return options;
  }, []);

  const currencySelectOptions = React.useMemo(() => {
    const options: SelectItem[] = [];
    for (const [value, label] of Object.entries(Currency)) {
      options.push({ label: toUpper(label), value });
    }

    return options;
  }, []);

  const billingCycleOptions = React.useMemo(() => {
    const options: SelectItem[] = [];
    for (const [value, label] of Object.entries(BillingCycle)) {
      if (isBitbucketProductEnabled && get(product, 'id') === BITBUCKET_PRODUCT_ID) {
        if (value === BillingCycle.ANNUAL) {
          options.push({ label: capitalize(label as string), value });
        }
      } else {
        options.push({ label: capitalize(label as string), value });
      }
    }

    return options;
  }, []);

  const defaultFiltersOptions = {
    type: SyntheticPlanType.COMMERCIAL,
    billingFrequency: showDefaultAnnualPricing ? BillingCycle.ANNUAL : BillingCycle.MONTHLY,
    currency: Currency.USD,
  };

  const availableOfferings = React.useMemo(() => {
    return defaultTo(get(availableOfferingForProduct, 'nativeCatalogItem.offering'), []);
  }, [availableOfferingForProduct]);

  const offeringOptions = React.useMemo(() => {
    return defaultTo(availableOfferings, [])
      .filter(
        (offeringItem: ProductOffering) =>
          isOfferingIncluded(offeringItem, {
            productId: defaultTo(get(product, 'id'), ''),
            isEnterpriseOfferingEnabled,
            isJSMRepackagingEnabled,
            isCompassPremiumEnabled,
            isJPDPremiumOfferingEnabled,
            isJPDAsJiraFamilyEnabled,
            isAppEditionSupportEnabled,
            isMultiInstanceAppEnabled,
            isBitbucketProductEnabled,
          }) && isValidOffering(defaultTo(get(offeringItem, 'offeringName'), ''))
      )
      .map((offeringItem: ProductOffering) => ({
        label: defaultTo(get(offeringItem, 'offeringName'), 'No name'),
        value: defaultTo(get(offeringItem, 'id'), ''),
      }));
  }, [availableOfferings]);

  const getOfferingName = (offeringId: string) => {
    let offeringName = 'No name';
    if (offeringId && offeringOptions?.length > 0) {
      offeringName = offeringOptions.find((option: SelectItem) => option.value === offeringId)?.label ?? offeringName;
    }
    return offeringName;
  };

  // default offering option should be selected to Standard but in case no standard offering found default to first offering
  const defaultOfferingOption =
    offeringOptions.find((availableOffering: SelectItem) =>
      availableOffering.label?.includes(capitalize(EntitlementEdition.STANDARD))
    ) ?? offeringOptions?.[0];

  React.useEffect(() => {
    setCurrentOffering(defaultOfferingOption?.value ?? '');
    setCurrentFilter(defaultFiltersOptions);
  }, [defaultOfferingOption]);

  React.useEffect(() => {
    (async () => {
      if (get(product, 'id') && currentOffering) {
        setCurrentPricingPlan(null);
        const { data } = await fetchAvailablePricingPlans({
          variables: {
            id: defaultTo(get(product, 'id'), ''),
            filterOptions: [
              {
                key: 'currency',
                value: currentCurrency,
              },
              {
                key: 'offeringId',
                value: currentOffering,
              },
              {
                key: 'billingFrequency',
                value: currentFrequency,
              },
            ],
            queryDefaultPricingPlans: true,
          },
        });
        setAvailablePricingPlans(data);
      }
    })();
  }, [currentOffering, currentFrequency, currentCurrency]);

  const renderPricingPlanTable = () => {
    if (!currentOffering) return null;
    if (called) {
      if (!currentPricingPlan) {
        if (isVersion2) {
          return getPricingPlanPanel({
            pricingPlans: pricingPlanOptions,
            currentFilter: currentFilter,
            product: product,
          });
        }
        return (
          <PricingPlanTable
            pricingPlans={pricingPlanOptions}
            error={error?.message}
            loading={pricingPlanLoading}
            currentFilter={currentFilter}
            setCurrentPricingPlan={setCurrentPricingPlan}
          />
        );
      } else return <PricingTiersTable pricingPlan={currentPricingPlan} product={product} />;
    }

    return null;
  };

  const resetFilters = () => {
    setCurrentOffering(defaultOfferingOption?.value ?? '');
    setCurrentFilter(defaultFiltersOptions);
    setCurrentFrequency(showDefaultAnnualPricing ? BillingCycle.ANNUAL : BillingCycle.MONTHLY);
    setCurrentCurrency(Currency.USD);
    setCurrentPricingPlan(null);
  };

  const getPricingHeader = () => {
    return (
      <div className="pricing-header">
        <div className="entitlement-name-with-icon">
          <EntitlementIcon
            logoId={defaultTo(get(product, 'logoId'), '')}
            productId={defaultTo(get(product, 'id'), '')}
            className="header-icon"
            isCloud={true}
          />
          <div className="entitlement-name">{defaultTo(get(product, 'name'), 'No name')}</div>
        </div>
        <Flex gap="space.100" alignItems="center">
          {hasOptedInForPartnerPromotion && (
            <div className="discount-indication-flag">
              <img
                // @ts-ignore
                src={PartnerDiscountIndicationIcon}
                alt={'Partner discount available'}
                className="discount-indication-flag-img"
              />
              Partner discount available
            </div>
          )}
          {isTWCPhase2Enabled && isVersion2 && isCollectionProductEntitlement(defaultTo(get(product, 'id'), '')) && (
            <OpenSidebarButton
              sidebar={CATALOGUE_CONSTANTS.Sidebar.INCLUDED_PRODUCTS}
              testId="included-products-sidebar-button-catalogue"
              label="Included products"
              payload={{ productKey: defaultTo(get(product, 'id'), ''), isVersion2 }}
            >
              View included products
            </OpenSidebarButton>
          )}
          {isTWCPhase2Enabled && !isVersion2 && (
            <Popup
              isOpen={isIncludedProductsPopupOpen}
              onClose={() => setIsIncludedProductsPopupOpen(false)}
              trigger={(triggerProps) => {
                return (
                  <Button
                    {...triggerProps}
                    onClick={() => setIsIncludedProductsPopupOpen(!isIncludedProductsPopupOpen)}
                  >
                    View included products
                  </Button>
                );
              }}
              content={() => (
                <Box
                  xcss={xcss({
                    minWidth: '300px',
                    minHeight: '200px',
                  })}
                  padding="space.200"
                >
                  <IncludedProductsSidebar productKey={defaultTo(get(product, 'id'), '')} isVersion2={isVersion2} />
                </Box>
              )}
              placement="bottom-end"
              zIndex={505}
            />
          )}
        </Flex>
      </div>
    );
  };

  const renderPricingPlanContent = () => {
    if (isVersion2 && (pricingPlanLoading || loadingOfferingForProduct)) {
      return <CollapsibleSummaryTableLoader tableColumnCount={3} />;
    } else if (!isVersion2 && pricingPlanLoading) {
      return <LoadingIndicator />;
    }
    return renderPricingPlanTable();
  };

  return (
    <div className="product-pricing-pane" data-testid="product-pricing-pane">
      {!isVersion2 && (
        <Button
          appearance="link"
          className="pad-left-0"
          testId="back-button"
          onClick={() => {
            if (!currentPricingPlan) {
              closeSidebar();
            } else {
              setCurrentPricingPlan(null);
            }
          }}
        >
          <ArrowLeft label="arrow-left" />
        </Button>
      )}
      {!isVersion2 && <h2>Pricing plan details</h2>}
      {isVersion2 ? <StyledPricingHeader>{getPricingHeader()}</StyledPricingHeader> : getPricingHeader()}
      <div className="pricing-subheader">
        <div data-testid="select-offering-field">
          <Select<SelectItem>
            isSearchable={false}
            options={offeringOptions}
            isLoading={loadingOfferingForProduct}
            placeholder="Select offering"
            className="select-item select-offering-field"
            value={
              !currentOffering
                ? undefined
                : {
                    value: defaultTo(currentOffering, ''),
                    label: getOfferingName(defaultTo(currentOffering, '')),
                  }
            }
            onChange={(item: ValueType<SelectItem, false>) => {
              setCurrentOffering(item?.value);
            }}
          />
        </div>
        &nbsp;&nbsp;
        <Select<SelectItem>
          isSearchable={false}
          options={syntheticPlanSelectOptions}
          placeholder="Pricing eligibility"
          className="select-item"
          data-testid="select-price-type-field"
          value={
            !get(currentFilter, 'type')
              ? undefined
              : {
                  value: defaultTo(get(currentFilter, 'type'), ''),
                  label: capitalize(
                    startCase(
                      SyntheticPlanType[defaultTo(get(currentFilter, 'type'), '') as keyof typeof SyntheticPlanType]
                    )
                  ),
                }
          }
          onChange={(item: ValueType<SelectItem, false>) => {
            setCurrentFilter({ ...currentFilter, type: defaultTo(get(item, 'value'), null) });
            setCurrentPricingPlan(null);
          }}
        />
        &nbsp;&nbsp;
        <Select<SelectItem>
          isSearchable={false}
          options={billingCycleOptions}
          placeholder="Billing cycle"
          className="select-item"
          data-testid="select-billing-cycle-field"
          value={
            !get(currentFilter, 'billingFrequency')
              ? undefined
              : {
                  value: defaultTo(
                    get(currentFilter, 'billingFrequency'),
                    showDefaultAnnualPricing ? BillingCycle.ANNUAL : BillingCycle.MONTHLY
                  ),
                  label: capitalize(
                    defaultTo(
                      get(currentFilter, 'billingFrequency'),
                      showDefaultAnnualPricing ? BillingCycle.ANNUAL : BillingCycle.MONTHLY
                    )
                  ),
                }
          }
          onChange={(item: ValueType<SelectItem, false>) => {
            setCurrentFilter({ ...currentFilter, billingFrequency: defaultTo(get(item, 'value'), '') });
            setCurrentFrequency(defaultTo(get(item, 'value'), BillingCycle.MONTHLY));
          }}
        />
        &nbsp;&nbsp;
        <Select<SelectItem>
          isSearchable={false}
          options={currencySelectOptions}
          placeholder="Currency"
          className="select-item"
          data-testid="select-currency-field"
          value={
            !get(currentFilter, 'currency')
              ? undefined
              : {
                  value: defaultTo(get(currentFilter, 'currency'), ''),
                  label: Currency[defaultTo(get(currentFilter, 'currency'), '') as keyof typeof Currency],
                }
          }
          onChange={(item: ValueType<SelectItem, false>) => {
            setCurrentFilter({ ...currentFilter, currency: defaultTo(get(item, 'value'), null) });
            setCurrentCurrency(defaultTo(get(item, 'value'), Currency.USD));
          }}
        />
        &nbsp;&nbsp;
        <Button onClick={resetFilters} appearance="link" testId="reset-filters">
          Reset filters
        </Button>
      </div>
      {renderPricingPlanContent()}
      <FormFooter align="end">
        {!isVersion2 && (
          <Button
            aria-label="Close view pricing"
            appearance="default"
            className={classNames({
              'cancel-view-pricing': isEnabled(ENABLE_DESIGN_ITERATION_1),
            })}
            onClick={closeSidebar}
          >
            Close
          </Button>
        )}
      </FormFooter>
    </div>
  );
};
